import { Box, Button, TextField } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CustomButton from '../../components/custom/btn/CustomButton';
import CustomCancelButton from '../../components/custom/btn/CustomCancelButton';
import CustomSuccessButton from '../../components/custom/btn/CustomSuccessButton';
import CustomBetCheckDialog from '../../components/custom/dialog/CustomBetCheckDialog';
import CustomLoading from '../../components/custom/loading/CustomLoading';
import CustomLoadingError from '../../components/custom/loading/CustomLoadingError';
import {
	showErrorSnackbar,
	showInfoSnackbar,
} from '../../components/custom/snackbar/snackbarSlice';
import useFetchCurrentUser from '../../components/hooks/useFetchCurrentUser';
import useFilterLanguageChange from '../../components/hooks/useFilterLanguageChange';
import { transformToGameScore } from '../../components/utils/gameScoreValidation';
import { BET_STATUS_LOST, BET_STATUS_RETURNED, BET_STATUS_WON, BetStatus } from '../../constants';
import { getActiveSeason, getActiveSeasonId } from '../admin/seasons/seasonsSlice';
import { selectActiveSeason, selectActiveSeasonId } from '../admin/seasons/selectors';
import OpenedBetCard from './OpenedBetCard';
import { betsLeagueFilterTabSx } from './betsPageStyles';
import { getOpenedBets, setBetResult } from './betsSlice';
import { selectOpenedBets } from './selectors';
import Bet from './types/Bet';
import GameScore from './types/GameScore';

export default function BetsCheck(): JSX.Element {
	const activeSeasonId = useAppSelector(selectActiveSeasonId);
	const activeSeason = useAppSelector(selectActiveSeason);
	const dispatch = useAppDispatch();
	const openedBets = useAppSelector(selectOpenedBets);
	const [selectedBet, setSelectedBet] = useState<Bet | undefined>(undefined);
	const [gameScoreInput, setGameScoreInput] = useState<string>();
	const [gameScore, setGameScore] = useState<GameScore | undefined>(undefined);
	const [inputValues, setInputValues] = useState<Record<string, string>>({});
	const [selectedLeague, setSelectedLeague] = useState(t('all'));
	const [dialogType, setDialogType] = useState<BetStatus | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [loadingError, setLoadingError] = useState(false);

	useFilterLanguageChange(setSelectedLeague);
	useFetchCurrentUser();

	const handleCloseDialog = (): void => {
		setDialogType(undefined);
	};

	const handleLeagueChange = (leagueName: string): void => {
		setSelectedLeague(leagueName);
	};

	const handleSaveBetCheck = useCallback(
		async (betStatus: string) => {
			handleCloseDialog();

			if (selectedBet) {
				const dispatchResult = await dispatch(
					setBetResult({
						betId: selectedBet.id,
						betResult: { gameScore, betStatus },
					})
				);

				if (setBetResult.fulfilled.match(dispatchResult)) {
					dispatch(showInfoSnackbar({ message: t('betWasSuccessfullyProcessed') }));
					setGameScore(undefined);
					handleLeagueChange(selectedLeague);
				}
				if (setBetResult.rejected.match(dispatchResult)) {
					dispatch(showErrorSnackbar({ message: dispatchResult.error.message }));
				}
			}
		},
		[dispatch, gameScore, selectedBet, selectedLeague]
	);

	const handleBetSave = (status: string): void => {
		handleSaveBetCheck(status);
	};

	const handleGameScoreChange = (betId: string, value: string): void => {
		setInputValues((prevGameScore) => ({
			...prevGameScore,
			[betId]: value,
		}));
	};

	const openDialog = (type: BetStatus, bet: Bet, result: string): void => {
		setGameScoreInput(result);
		const res = transformToGameScore(result);
		setGameScore(res);
		setSelectedBet(bet);
		setDialogType(type);
	};

	useEffect(() => {
		if (!activeSeasonId) {
			dispatch(getActiveSeasonId());
		}
		if (!activeSeason) {
			dispatch(getActiveSeason());
		}
	}, []);

	useEffect(() => {
		if (activeSeasonId) {
			dispatch(getOpenedBets({ seasonId: activeSeasonId }))
				.then(() => {
					setLoading(false);
				})
				.catch(() => {
					setLoadingError(true);
					setLoading(false);
				});
		}
	}, [activeSeasonId]);

	return (
		<Box>
			{loading ? (
				<CustomLoading />
			) : (
				<Box>
					{loadingError ? (
						<CustomLoadingError />
					) : (
						<Box sx={{ display: 'flex', justifyContent: 'center' }}>
							{activeSeason && (
								<Box>
									<Box sx={{ borderBottom: 2, textAlign: 'center', mx: 3, pb: 0.5, mb: 1.5 }}>
										{t('betsCheck')}
									</Box>

									<Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
										<Button
											color="inherit"
											sx={betsLeagueFilterTabSx(selectedLeague === t('all'))}
											onClick={() => handleLeagueChange(t('all'))}
										>
											{t('all')}
										</Button>
										{activeSeason.leagues?.map((l) => (
											<Button
												key={l.leagueCode}
												color="inherit"
												sx={betsLeagueFilterTabSx(selectedLeague === l.leagueCode)}
												onClick={() => handleLeagueChange(l.leagueCode)}
											>
												{t(`leagueShortName.${l.leagueCode}`)}
											</Button>
										))}
									</Box>

									{openedBets.map((bet) => {
										if (bet.leagueCode === selectedLeague || selectedLeague === t('all')) {
											return (
												<Box key={bet.id}>
													<OpenedBetCard bet={bet} />
													<Box
														sx={{
															mb: 3,
															mt: 0.5,
															ml: 0.5,
															display: 'flex',
															justifyContent: 'center',
														}}
													>
														<Box>
															<Box sx={{ mb: 0.5, px: 0.5 }}>
																<TextField
																	fullWidth
																	required
																	id={`inputValues-${bet.id}`}
																	label={t('gameScore')}
																	variant="outlined"
																	value={inputValues[bet.id] || ''}
																	onChange={(event) =>
																		handleGameScoreChange(bet.id, event.target.value)
																	}
																/>
															</Box>
															<Box>
																<CustomCancelButton
																	sx={{ px: 2 }}
																	onClick={() =>
																		openDialog(BET_STATUS_LOST, bet, inputValues[bet.id])
																	}
																	buttonText={t('lost')}
																	textSize="0.8rem"
																/>
																<CustomButton
																	sx={{
																		height: '2rem',
																		mr: 1,
																		bgcolor: 'yellow',
																		color: 'black',
																		px: 2,
																	}}
																	onClick={() =>
																		openDialog(BET_STATUS_RETURNED, bet, inputValues[bet.id])
																	}
																	buttonText={t('returned')}
																	textSize="0.8rem"
																/>
																<CustomSuccessButton
																	sx={{ px: 2 }}
																	onClick={() =>
																		openDialog(BET_STATUS_WON, bet, inputValues[bet.id])
																	}
																	buttonText={t('won')}
																	textSize="0.8rem"
																/>
															</Box>
														</Box>
													</Box>
												</Box>
											);
										}
									})}
								</Box>
							)}

							<CustomBetCheckDialog
								open={dialogType === BET_STATUS_LOST}
								onClose={handleCloseDialog}
								onSave={() => handleBetSave(BET_STATUS_LOST)}
								gameScoreInput={gameScoreInput}
								bet={selectedBet}
								buttonColor="error"
								buttonText={t('lost')}
							/>

							<CustomBetCheckDialog
								open={dialogType === BET_STATUS_RETURNED}
								onClose={handleCloseDialog}
								onSave={() => handleBetSave(BET_STATUS_RETURNED)}
								gameScoreInput={gameScoreInput}
								bet={selectedBet}
								buttonText={t('returned')}
								sx={{ bgcolor: '#e6eb16', color: 'black' }}
							/>

							<CustomBetCheckDialog
								open={dialogType === BET_STATUS_WON}
								onClose={handleCloseDialog}
								onSave={() => handleBetSave(BET_STATUS_WON)}
								gameScoreInput={gameScoreInput}
								bet={selectedBet}
								buttonColor="success"
								buttonText={t('won')}
							/>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
}
